import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminDiscount, useDeactivateDiscount, useUpdateDiscount } from "@/hooks/useAdminData";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export function AdminDiscountDetail() {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { data: discount, isLoading } = useAdminDiscount(id ?? "");
	const updateMutation = useUpdateDiscount(id ?? "");
	const deactivateMutation = useDeactivateDiscount(id ?? "");

	const [name, setName] = useState<string | null>(null);

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton data-testid="skeleton" className="h-6 w-64" />
				<Skeleton data-testid="skeleton" className="h-8 w-48" />
				<Skeleton data-testid="skeleton" className="h-48 rounded-xl" />
			</div>
		);
	}

	if (!discount) {
		return <div className="py-12 text-center text-secondary">{t("admin.discountNotFound")}</div>;
	}

	const currentName = name ?? discount.name ?? "";

	function handleSaveName() {
		if (currentName === (discount?.name ?? "")) return;
		updateMutation.mutate(
			{ name: currentName },
			{
				onSuccess: () => toast.success(t("admin.discountUpdated")),
				onError: () => toast.error(t("admin.saveFailed")),
			},
		);
	}

	function handleToggleActive() {
		updateMutation.mutate(
			{ active: !discount?.active },
			{
				onSuccess: () => toast.success(t("admin.discountUpdated")),
				onError: () => toast.error(t("admin.saveFailed")),
			},
		);
	}

	function handleDeactivate() {
		deactivateMutation.mutate(undefined, {
			onSuccess: () => {
				toast.success(t("admin.discountDeactivated"));
				navigate("/admin/discounts");
			},
			onError: () => toast.error(t("admin.saveFailed")),
		});
	}

	return (
		<div className="space-y-6">
			<AdminBreadcrumb
				items={[
					{ label: t("admin.dashboard"), href: "/admin" },
					{ label: t("admin.discounts"), href: "/admin/discounts" },
					{ label: discount.name ?? discount.code },
				]}
			/>

			<div className="flex items-center justify-between">
				<h1 className="text-3xl font-bold text-primary">
					<span className="font-mono">{discount.code}</span>
				</h1>
				<Badge
					className={
						discount.active
							? "border-transparent bg-success-muted text-success"
							: "border-transparent bg-error-muted text-error"
					}
				>
					{discount.active ? t("admin.active") : t("admin.inactive")}
				</Badge>
			</div>

			{/* Info card */}
			<div className="rounded-xl border border-white/10 bg-bg-elevated p-6">
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
					<div>
						<p className="text-xs text-muted">{t("admin.type")}</p>
						<p className="font-medium capitalize text-primary">{discount.type}</p>
					</div>
					<div>
						<p className="text-xs text-muted">{t("admin.amount")}</p>
						<p className="font-medium text-primary">
							{discount.type === "percentage"
								? `${discount.percent_off}%`
								: `$${((discount.amount_off ?? 0) / 100).toFixed(2)}`}
						</p>
					</div>
					<div>
						<p className="text-xs text-muted">{t("admin.uses")}</p>
						<p className="font-medium text-primary">
							{discount.times_redeemed}
							{discount.max_redemptions ? ` / ${discount.max_redemptions}` : ""}
						</p>
					</div>
					<div>
						<p className="text-xs text-muted">{t("admin.expiryDate")}</p>
						<p className="font-medium text-primary">
							{discount.expires_at
								? new Date(discount.expires_at * 1000).toLocaleDateString()
								: "-"}
						</p>
					</div>
				</div>
			</div>

			{/* Edit section */}
			<div className="max-w-2xl space-y-6 rounded-xl border border-white/10 bg-bg-elevated p-6">
				<h2 className="text-lg font-semibold text-primary">{t("admin.editDiscount")}</h2>

				<div className="space-y-2">
					<Label htmlFor="name">{t("admin.description")}</Label>
					<Input
						id="name"
						value={currentName}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("admin.discountDescPlaceholder")}
					/>
				</div>

				<div className="flex flex-wrap gap-3">
					<Button
						onClick={handleSaveName}
						disabled={updateMutation.isPending || currentName === (discount.name ?? "")}
					>
						{t("admin.saveDescription")}
					</Button>
					<Button
						variant="outline"
						onClick={handleToggleActive}
						disabled={updateMutation.isPending}
					>
						{discount.active ? t("admin.deactivateDiscount") : t("admin.activateDiscount")}
					</Button>
					{discount.active && (
						<Button
							variant="destructive"
							onClick={handleDeactivate}
							disabled={deactivateMutation.isPending}
						>
							{t("admin.deactivateAndReturn")}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
